import { DisplayMetric } from 'src/components/dispatch/unassignedDisplay/displayMetricKeys';
import { LoadTotals, ZERO_LOAD } from 'src/components/dispatch/vehicleLoad';
import { hasAssignedDelivery, hasAssignedLoading } from 'src/components/dispatch/vehicleRowExpand/assignedDelivery';
import { ExpandedRowVM, PlanWindowSource, TerminalSource, buildExpandedRowVM } from 'src/components/dispatch/vehicleRowExpand/buildExpandedRowVM';
import { OperationSourceCardInput, buildOperationPlaces } from 'src/components/dispatch/vehicleRowExpand/buildOperationPlaces';
import { VehicleRowGroup } from 'src/pages/ConvenienceDispatches/adapter';
import { VehicleRowVM } from 'src/pages/ConvenienceDispatches/vehicleRowVM';

/**
 * VehicleRowCardVM を中立入力に詰め替える。
 * lat/lng と地点名・住所は operation が持たないので legRun から引く（buildPlaceKey と同じ導出）。
 * 地点名・住所を operation ごとに渡すのは、1 カードが複数停車に分かれるときに
 * 行ごとの名義を出すため（log_web_issues#314）。
 */
function toSourceCards(cards: VehicleRowVM['cards']): OperationSourceCardInput[] {
  return cards.map((c) => ({
    placeKey: c.placeKey,
    kind: c.kind,
    placeName: c.placeName,
    operations: c.operations.map((op) => {
      // find は必ず一致する（id が無い legRun は legRunMap に入らず (vehicleRowVM.ts:339)
      // groupOpsToCards にも渡らないため、カードの legRuns は自身の id で引ける legRun しか
      // 持たない）。undefined になるのは operation に対応する legRun が無い場合だけで、
      // その operation は専用カード（legRuns 空）を持つため座標は両方 null になる。
      const lr = c.legRuns.find((it) => it.id === op.shipmentOrderLegRunId);
      const isLoading = op.action === '積';
      return {
        id: op.id,
        action: op.action,
        arrivalAt: op.arrivalAt,
        departureAt: op.departureAt,
        operationDurationSeconds: op.operationDurationSeconds,
        waitingDurationSeconds: op.waitingDurationSeconds,
        drivingDurationSeconds: op.drivingDurationSeconds,
        shipmentOrderLegRunId: op.shipmentOrderLegRunId,
        lat: isLoading ? lr?.loading_lat ?? null : lr?.unloading_lat ?? null,
        lng: isLoading ? lr?.loading_lng ?? null : lr?.unloading_lng ?? null,
        locationName: isLoading ? lr?.loading_name ?? null : lr?.unloading_name ?? null,
        address: isLoading ? lr?.loading_address ?? null : lr?.unloading_address ?? null,
      };
    }),
    legRuns: c.legRuns,
  }));
}

/**
 * 展開行の VM を rowId 単位で作る。
 *
 * 1 箇所で作るのは、展開パネルと地図マーカーが同じ地点番号(seriesIndex)を
 * 出す必要があるため。行ごと・画面ごとに計算すると必ず食い違う。
 *
 * seriesIndex は /plans と同じく回転をまたいだ通し番号にする
 * (PlanningsOperationDelivery:89-95)。/plans の勤務(delivery)が
 * /map の 1 truck の VehicleRowGroup に対応する。
 */
export function buildExpandedRowVMByRowId(params: {
  rowGroups: readonly VehicleRowGroup[];
  displayMetrics: ReadonlySet<DisplayMetric>;
  /** 回転行ごとの積載量。勤務全体を走査する buildPeakLoadByRowId が呼び出し側で作る */
  peakLoadByRowId: ReadonlyMap<string, LoadTotals>;
  resolveGarage?: (deliveryId: number) => { name: string; address?: string } | undefined;
  /** delivery の departure_operation / arrival_operation。出発・帰着の実績地点と時刻 */
  resolveTerminals?: (deliveryId: number) => { departure?: TerminalSource; arrival?: TerminalSource } | undefined;
  /**
   * 勤務 (delivery) 全体の計画の窓。ヘッダーの「計画」に出す。
   * 1 delivery が cycle ごとに複数 row になるので、同じ勤務の行はすべて同じ値になる
   * （旧マップ /plans は勤務単位で 1 つしか出さない・log_web_issues#310）
   */
  resolvePlanWindow?: (deliveryId: number) => PlanWindowSource | undefined;
}): Map<string, ExpandedRowVM> {
  const { rowGroups, displayMetrics, peakLoadByRowId, resolveGarage, resolveTerminals, resolvePlanWindow } = params;
  const result = new Map<string, ExpandedRowVM>();

  for (const group of rowGroups) {
    // 「実配送がある行」は配車済みの降地か積の operation がある行。積だけの勤務（宵積みの
    // 1 日目）も車庫を出て戻るので出発・帰着の行が要る (log_web_issues#343)
    const activeRows = group.rows.map((r) => hasAssignedDelivery(r.cards) || hasAssignedLoading(r.source));
    // 出発の行と計画開始は「その車両で最初に実配送がある行」を基準にする。
    // rowIndex === 0 だと先頭が placeholder のとき出発の行がどこにも出ない。
    const firstActiveRowIndex = activeRows.indexOf(true);
    // 帰着は最後の実配送行に置く。全行に出すと 2 回転ある勤務で 1 回転目の末尾に帰庫が入り、
    // 「出発・積・降・帰着・積・降」と読めてしまう
    const lastActiveRowIndex = activeRows.lastIndexOf(true);
    let seriesIndexBase = 0;

    group.rows.forEach((row, rowIndex) => {
      const { places, quantities } = buildOperationPlaces(toSourceCards(row.source.cards), row.cards);
      const terminals = row.source.isPlaceholderRow ? undefined : resolveTerminals?.(row.source.deliveryId);
      result.set(row.rowId, buildExpandedRowVM({
        places,
        quantities,
        displayMetrics,
        cards: row.cards,
        peakLoad: peakLoadByRowId.get(row.rowId) ?? ZERO_LOAD,
        isFirstActiveRow: rowIndex === firstActiveRowIndex,
        isLastActiveRow: rowIndex === lastActiveRowIndex,
        seriesIndexBase,
        planStartAt: row.source.planStartAt,
        // placeholder 行の deliveryId は実在しないダミー。resolveTerminals と同じく引かない
        planWindow: row.source.isPlaceholderRow ? undefined : resolvePlanWindow?.(row.source.deliveryId),
        garage: row.source.isPlaceholderRow ? undefined : resolveGarage?.(row.source.deliveryId),
        departure: terminals?.departure,
        arrival: terminals?.arrival,
      }));
      seriesIndexBase += places.length;
    });
  }

  return result;
}
